import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { softseePackagesPageQuery, type SoftseePackageJob } from '../api/devices'
import { ScheduleInstallForm } from '../softsee/ScheduleInstallForm'
import { StopScheduledInstall } from '../softsee/StopScheduledInstall'
import { Modal } from '../ui/Modal'
import { Spinner } from '../ui/Spinner'
import { SoftseeInstallList } from './DeviceSoftseeInstall'
import type { ScreenProps } from './screens'

/**
 * Fila Install Packages - softsee/devices/details/devicePackages.php.
 * Packages e lista programata. Kits / Custom Kits arata lista de instalare
 * cu sursa aleasa aici, fara al doilea comutator al filei Install
 * Applications: pana pe 18 septembrie 2026 fila aia se desena intreaga
 * inauntru, cu comutatorul ei pe Kits, iar butonul Custom Kits de aici
 * schimba doar o eticheta pe care n-o citea nimeni.
 *
 * "Schedule a package" si "Stop" SCRIU, ca in portalul vechi: fara ele fila
 * arata ce e programat pe statie, dar nu se putea nici adauga, nici opri
 * nimic de aici - iar omul intrat pe o statie trebuia sa iasa, s-o caute in
 * lista si s-o bifeze, exact drumul pe care fila asta il scurtase.
 *
 * Formularul sta intr-un popup, nu sub tabel: fila traieste in randul
 * desfasurat, si un panou crescut acolo impinge campurile sub marginea de jos
 * a ecranului.
 */

export function DeviceSoftseePackages({ id }: ScreenProps) {
  const client = useQueryClient()
  const page = useQuery(softseePackagesPageQuery(id))
  const [source, setSource] = useState<'packages' | 'kits' | 'custom_kits'>('packages')
  const [adding, setAdding] = useState(false)

  /* Fila se reciteste dupa fiecare scriere: altfel omul programeaza ceva, vede
     "Scheduled" si se uita la o lista in care nu apare. */
  const reload = () => {
    void client.invalidateQueries({ queryKey: ['device', id, 'softsee-packages'] })
  }

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const mine = source === 'packages'

  return (
    <div className="dp-tab" id={`dpTab${id}`}>
      <style>{PACKAGES_CSS}</style>
      <div className="row dp-toggle">
        <div className="col-md-12">
          <div className="btn-group toggle-kits" data-toggle="buttons">
            <SourceChip
              value="packages"
              active={source === 'packages'}
              onPick={() => setSource('packages')}
              icon="fas fa-box-open"
              label="Packages"
            />
            <SourceChip
              value="kits"
              active={source === 'kits'}
              onPick={() => setSource('kits')}
              icon="fas fa-cloud-download-alt"
              label="Kits"
            />
            <SourceChip
              value="custom_kits"
              active={source === 'custom_kits'}
              onPick={() => setSource('custom_kits')}
              icon="fas fa-cogs"
              label="Custom Kits"
            />
          </div>
        </div>
      </div>

      {mine && (
        <>
          <div className="dp-head">
            <h4>
              <strong>Install</strong> Packages
            </h4>
            <button type="button" className="btn btn-primary btn-sm" id="dpAdd" onClick={() => setAdding(true)}>
              <i className="fas fa-plus"></i> Schedule a package
            </button>
          </div>
          <div id="dpPackages">
            {page.data.jobs.length === 0 ? (
              <div className="dp-none">
                <i className="fas fa-info-circle"></i> {page.data.empty}
              </div>
            ) : (
              <JobsTable jobs={page.data.jobs} onStopped={reload} />
            )}
          </div>
        </>
      )}

      <Modal
        id={`dpModal${id}`}
        open={adding}
        onClose={() => setAdding(false)}
        title={
          <>
            <strong>Schedule</strong> package installation
          </>
        }
      >
        {/* `onlyDevice`: intrebarea "pe care statii?" are deja raspuns aici,
            deci lista de statii nu se mai deseneaza deloc. */}
        {adding ? (
          <ScheduleInstallForm
            onlyDevice={id}
            onSaved={() => {
              setAdding(false)
              reload()
            }}
          />
        ) : null}
      </Modal>

      {!mine && (
        <div id="dpKits">
          {/* `key`: lista pleaca de la prima pagina si fara cautare cand se schimba sursa. */}
          <SoftseeInstallList key={source} id={id} door="softsee" source={source} />
        </div>
      )}
    </div>
  )
}

function SourceChip({
  value,
  active,
  onPick,
  icon,
  label,
}: {
  value: string
  active: boolean
  onPick(): void
  icon: string
  label: string
}) {
  return (
    <label className={`btn btn-theme-inverse btn-transparent${active ? ' active' : ''}`}>
      <input type="radio" name="dpSource" autoComplete="off" value={value} checked={active} onChange={onPick} />
      <i className={icon}></i> {label}
    </label>
  )
}

function JobsTable({ jobs, onStopped }: { jobs: SoftseePackageJob[]; onStopped(): void }) {
  return (
    <table className="table table-striped table-bordered" width="100%">
      <thead>
        <tr>
          <th>Package</th>
          <th>Starts</th>
          <th>Deadline</th>
          <th>How often</th>
          <th>Last run</th>
          <th
            data-toggle="tooltip"
            data-placement="top"
            title="Whether the schedule itself is still alive - not how the last install went."
          >
            Schedule <i className="fas fa-info-circle" style={{ fontSize: 10, color: '#98a1aa' }}></i>
          </th>
          <th style={{ width: 80 }}></th>
        </tr>
      </thead>
      <tbody>
        {jobs.map((job) => (
          <tr key={job.id}>
            <td>{job.package}</td>
            <td>{job.starts}</td>
            <td>
              {job.deadline !== '' ? (
                job.deadline
              ) : (
                <span
                  className="dp-muted"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Without a deadline the agent asks the person at the computer to restart, instead of restarting on its own."
                >
                  none <i className="fas fa-info-circle"></i>
                </span>
              )}
            </td>
            <td>{job.how}</td>
            <td>
              {job.last_label !== '' ? (
                <>
                  <span
                    style={job.withdrawn ? { color: '#98a1aa' } : undefined}
                    data-toggle="tooltip"
                    data-placement="top"
                    title={job.last_note}
                  >
                    {job.last_label}
                    {job.withdrawn && <i className="fas fa-ban" style={{ fontSize: 10 }}></i>}
                  </span>
                  <br />
                  <span className="dp-muted" style={{ fontSize: 11 }}>
                    {job.last_due}
                    {job.withdrawn && ' · command withdrawn'}
                  </span>
                </>
              ) : (
                <span className="dp-muted">not sent yet</span>
              )}
            </td>
            <td>
              <span className="dp-state" style={{ background: job.schedule_color }}>
                {job.schedule}
              </span>
              {job.device_count > 1 && (
                <>
                  <br />
                  <span className="dp-muted" style={{ fontSize: 11 }}>
                    shared with {job.device_count - 1} more
                  </span>
                </>
              )}
            </td>
            <td>
              {/* Opreste jobul intreg, nu doar statia asta - de-aia scrie alaturi
                  cu cate statii mai e impartit. */}
              {job.can_stop && <StopScheduledInstall id={job.id} onStopped={onStopped} />}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const PACKAGES_CSS = `
    .dp-head { display: flex; align-items: center; margin-bottom: 12px; }
    .dp-head h4 { margin: 0; flex: 1; }
    .dp-state { font-size: 11px; font-weight: 600; color: #fff; padding: 2px 8px; border-radius: 10px; }
    .dp-none { color: #98a1aa; padding: 18px 0; }
    .dp-muted { color: #98a1aa; }
    .dp-toggle { margin-bottom: 18px; }
`
